import React, { useRef, useState, useEffect } from 'react';
import { Printer, Upload } from 'lucide-react';
import apiService from '../config';
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';

const LandAllocationReport = () => {
  const documentRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [fullName, setFullName] = useState('');
  const [designation , setDesignation] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [pdfForSigning, setPdfForSigning] = useState(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewerFile, setViewerFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const {
      uploadStatus,
      uploadError,
      selectedSignDocument,
      resetSignatureState,
      setUploadStatus,
      setUploadError,
      signDocument
    } = useSignature();

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        setFullName(userData.full_name || '');
        setDesignation(userData.designation || '');
      }, []);

  // Get project & proposal IDs from localStorage
  useEffect(() => {
    const getProjectId = () => {
      const current = localStorage.getItem('currentProjectData');
      if (current) {
        const p = JSON.parse(current);
        return p.project_id || p.id;
      }
      const selected = localStorage.getItem('selectedProject');
      if (selected) {
        const p = JSON.parse(selected);
        return p.project_id || p.id;
      }
      const user = localStorage.getItem('userData');
      if (user) {
        const u = JSON.parse(user);
        return u?.data?.project_id || u?.project_id || null;
      }
      return null;
    };

    const getProposalId = () => {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return (
        userData?.data?.default_proposal ||
        userData?.data?.proposal_id ||
        userData?.default_proposal ||
        userData?.proposal_id ||
        null
      );
    };

    setProjectId(getProjectId());
    setProposalId(getProposalId());
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchReport = async () => {
      if (!projectId || !proposalId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await apiService.getLandAllocationReport(projectId, proposalId);

        let apiData = [];

        if (response?.data && Array.isArray(response.data)) {
          apiData = response.data;
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          apiData = response.data.data;
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        setReportData(apiData);
      } catch (err) {
        console.error('Error fetching land allocation report:', err);
        setReportData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [projectId, proposalId]);

  const handlePrint = () => {
    const printContent = documentRef.current?.innerHTML;
    if (!printContent) {
      alert("No content available to print.");
      return;
    }

    const printWindow = window.open('about:blank', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print - Land Allocation Report</title>
          <meta charset="UTF-8">
          <style>
            @page {
              size: A4 landscape;
              margin-top: 0.3in;
              margin-bottom: 0.3in;
              margin-left: 0.75in;
              margin-right: 0.6in;
            }
            
            @media print {
              .no-print {
                display: none !important;
              }
              .page-break {
                page-break-before: always;
              }
            }
            
            body {
              font-family: 'Noto Sans Devanagari', 'Mangal', 'Arial', sans-serif;
              font-size: 13px;
              line-height: 1.5;
              color: #000;
              margin: 0;
              padding: 20px;
            }

            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .mb-2 { margin-bottom: 4px; }
            .mb-4 { margin-bottom: 8px; }
            .mt-12 { margin-top: 48px; }

            table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-top: 10px;
            }

            th, td {
              border: 1px solid #000;
              padding: 4px 6px;
              text-align: center;
              vertical-align: middle;
            }

            th { font-weight: bold; }

            .signature{
            text-align : right;
            margin-top : 40px;
            }

            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleUploadedFile = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  resetSignatureState();     // 🔥 important
  setPdfForSigning(file);
  setShowSignaturePad(true);
};

 useEffect(() => {
  if (!selectedSignDocument) return;

  const postSignedPdf = async () => {
    try {
      const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
      const projectId = selectedProject?.id || selectedProject?.project_id;
      const proposalId = localStorage.getItem("currentProposalId");

      if (!projectId || !proposalId) {
        console.error("Missing projectId or proposalId");
        return;
      }

      await apiService.postAddSignaturePDF(
        projectId,
        proposalId,
        "LandReport",      // 🔁 change per section
        selectedSignDocument   // ✅ signed PDF only
      );

      const sectionRes = await apiService.getSectionFile(
        projectId,
        proposalId,
        "LandReport"
      );

      if (sectionRes?.isSignAvailable && sectionRes.data?.[0]?.file_path) {
        setPreviewUrl(sectionRes.data[0].file_path);
      }

    } catch (error) {
      console.error("Error posting signed PDF:", error);
    }
  };

  postSignedPdf();
}, [selectedSignDocument]);

const handleViewSignedPdf = async () => {
  try {
    const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = localStorage.getItem("currentProposalId");

    if (!projectId || !proposalId) {
      alert("Project or Proposal not found");
      return;
    }

    const res = await apiService.getSectionFile(
      projectId,
      proposalId,
      "LandReport" // 🔁 change per section
    );

    if (res?.isSignAvailable && res?.data?.[0]?.file_path) {
      setViewerFile(res.data[0].file_path);
      setIsViewerOpen(true);     // modal
      // OR 👉 window.open(res.data[0].file_path, "_blank");
    } else {
      alert("Signed document not available");
    }
  } catch (error) {
    console.error("Error viewing signed PDF:", error);
    alert("Unable to open signed document");
  }
};

const SignatureLoader = ({ text }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
      <p className="text-sm font-medium text-gray-700">{text}</p>
    </div>
  </div>
);

  // Calculate totals for a location group
  const calculateLocationTotals = (areaReports) => {
    return areaReports.reduce(
      (acc, item) => ({
        total_plots: acc.total_plots + (Number(item.total_no_plot) || 0),
        alloted: acc.alloted + (Number(item.alloted_plot) || 0),
        vacant: acc.vacant + (Number(item.vacant_plot) || 0),
      }),
      { total_plots: 0, alloted: 0, vacant: 0 }
    );
  };

  // Split vacant numbers into rows of 10
  const splitVacantNumbers = (vacantNumberString) => {
    if (!vacantNumberString) return [];
    
    const numbers = vacantNumberString
      .split(',')
      .map(n => n.trim())
      .filter(Boolean);
    
    const rows = [];
    for (let i = 0; i < numbers.length; i += 10) {
      rows.push(numbers.slice(i, i + 10));
    }
    
    return rows;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-lg font-medium">डेटा लोड होत आहे...</p>
      </div>
    );
  }

  if (reportData.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-xl font-medium text-gray-600">
          या प्रकल्पासाठी भूखंड वाटपाचा डेटा उपलब्ध नाही
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Print Button */}
      <div className="flex justify-start mb-6">

      {/* <button className="relative">
                  <label className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 cursor-pointer">
                    <Upload size={18} />
                    <span>Upload Signature</span>
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={handleUploadedFile}
                    />
                  </label>
                </button>
      
                <button
                onClick={handleViewSignedPdf}
                className="flex items-center mx-3 space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                <Printer size={18} />
                <span>View Signed PDF</span>
              </button> */}

        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg"
        >
          <Printer size={20} />
          Print
        </button>
      </div>

      {/* Document Preview */}
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div
          ref={documentRef}
          className="document-container"
          style={{
            fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
            padding: "30px",
            fontSize: "15px",
            lineHeight: "1.5"
          }}
        >
          {reportData.map((location, locationIndex) => {
            const totals = calculateLocationTotals(location.area_reports);
            
            return (
              <div key={locationIndex} className={locationIndex > 0 ? "mt-12 page-break" : ""}>
                {/* Header for each location */}
                <div className="text-center font-bold mb-2" style={{ fontSize: '15px' }}>
                  जिगांव प्रकल्प तालुका {location.taluka_name} जिल्हा {location.district_name}
                </div>
                <div className="text-center mb-4" style={{ fontSize: '15px' }}>
                  मौजे {location.village_name} टु. तालुका {location.taluka_name} जिल्हा {location.district_name} चे (नविन गावठाण मौजे {location.base_village_name} तालुका {location.base_taluka_name} जिल्हा {location.base_district_name}) " नमुना - ब " रजिस्टरनुसार<br/>
                  प्रकल्पप्रस्ताना वाटप झालेले भूखंड आणि रिक्त भूखंडाचा तपशिल
                </div>

                {/* Table */}
                <table className="w-full border-2 border-black">
                  <thead>
                    <tr className="font-bold">
                      <th className="border border-black p-2" rowSpan="2" style={{ width: '60px' }}>अ.क्र.</th>
                      <th className="border border-black p-2" rowSpan="2" style={{ width: '80px' }}>क्षेत्रफळ<br/>चौ.मी.</th>
                      <th className="border border-black p-2" rowSpan="2" style={{ width: '80px' }}>एकुण भूखंड<br/>संख्या</th>
                      <th className="border border-black p-2" rowSpan="2" style={{ width: '80px' }}>वाटप झालेले<br/>भूखंड संख्या</th>
                      <th className="border border-black p-2" rowSpan="2" style={{ width: '80px' }}>रिक्त असलेले<br/>भूखंड संख्या</th>
                      <th className="border border-black p-2" colSpan="10">रिक्त असलेले भूखंडाचे क्रमांक</th>
                    </tr>
                    <tr className="font-bold">
                      <th className="border border-black p-1" colSpan="10">6</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="font-bold text-center">
                      <td className="border border-black p-2">1</td>
                      <td className="border border-black p-2">2</td>
                      <td className="border border-black p-2">3</td>
                      <td className="border border-black p-2">4</td>
                      <td className="border border-black p-2">5</td>
                      <td className="border border-black p-2" colSpan="10"></td>
                    </tr>

                    {location.area_reports.map((item, index) => {
                      const vacantRows = splitVacantNumbers(item.vacant_plot_number);
                      const rowCount = Math.max(1, vacantRows.length);

                      return (
                        <React.Fragment key={index}>
                          {vacantRows.length === 0 ? (
                            // No vacant plots - single row
                            <tr>
                              <td className="border border-black p-2">{index + 1}</td>
                              <td className="border border-black p-2">{Number(item.area_sq_mtr).toFixed(2)}</td>
                              <td className="border border-black p-2">{item.total_no_plot}</td>
                              <td className="border border-black p-2">{item.alloted_plot}</td>
                              <td className="border border-black p-2">{item.vacant_plot}</td>
                              {Array.from({ length: 10 }).map((_, i) => (
                                <td key={i} className="border border-black p-1"></td>
                              ))}
                            </tr>
                          ) : (
                            // Has vacant plots - multiple rows if needed
                            vacantRows.map((row, rowIndex) => (
                              <tr key={`${index}-${rowIndex}`}>
                                {rowIndex === 0 && (
                                  <>
                                    <td className="border border-black p-2" rowSpan={rowCount}>{index + 1}</td>
                                    <td className="border border-black p-2" rowSpan={rowCount}>{Number(item.area_sq_mtr).toFixed(2)}</td>
                                    <td className="border border-black p-2" rowSpan={rowCount}>{item.total_no_plot}</td>
                                    <td className="border border-black p-2" rowSpan={rowCount}>{item.alloted_plot}</td>
                                    <td className="border border-black p-2" rowSpan={rowCount}>{item.vacant_plot}</td>
                                  </>
                                )}
                                {row.map((num, i) => (
                                  <td key={i} className="border border-black p-1">{num}</td>
                                ))}
                                {Array.from({ length: 10 - row.length }).map((_, i) => (
                                  <td key={`empty-${i}`} className="border border-black p-1"></td>
                                ))}
                              </tr>
                            ))
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* Total Row */}
                    <tr className="font-bold">
                      <td className="border border-black p-2" colSpan="2">एकूण</td>
                      <td className="border border-black p-2">{totals.total_plots}</td>
                      <td className="border border-black p-2">{totals.alloted}</td>
                      <td className="border border-black p-2">{totals.vacant}</td>
                      <td className="border border-black p-2" colSpan="10"></td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-10 text-right signature">
          <div className="font-bold">{fullName}</div>
          <div>{designation}</div>
        </div>
              </div>
            );
          })}
        </div>
      </div>
       {showSignaturePad && pdfForSigning && (
  <SignaturePad
    pdfFile={pdfForSigning}
    onSign={(signaturesArray) => {
      setShowSignaturePad(false);

      // ✅ ONLY sign here
      signDocument(pdfForSigning, signaturesArray);

      setPdfForSigning(null);
    }}
    onCancel={() => {
      setShowSignaturePad(false);
      setPdfForSigning(null);
    }}
  />
)}

{isViewerOpen && viewerFile && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
    <div className="bg-white rounded-lg w-[80%] max-w-6xl h-[80%] relative">

      <div className="flex justify-between items-center px-4 py-2 border-b">
        <h3 className="text-lg font-semibold">Signed Document</h3>
        <button
          onClick={() => setIsViewerOpen(false)}
          className="text-xl font-bold"
        >
          ✕
        </button>
      </div>

      <iframe
        src={viewerFile}
        title="Signed PDF Viewer"
        className="w-full h-full"
      />
    </div>
  </div>
)}

{(uploadStatus === "signing" || uploadStatus === "uploading") && (
  <SignatureLoader
    text={
      uploadStatus === "signing"
        ? "Applying digital signature…"
        : "Uploading signed document…"
    }
  />
)}
    </div>
  );
};

export default LandAllocationReport;